import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { MapPin, Loader2, RefreshCw, ExternalLink, AlertTriangle, FileText, Home, Euro, Map, BarChart3, ShieldAlert, MapPinned } from 'lucide-react';
import { toast } from 'sonner';
import { PropertyMap } from './PropertyMap';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const fmtEur = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR')} €`);
const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('fr-FR');
};

const SectionCard = ({ icon: Icon, title, color, children, action }) => (
  <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4">
    <div className="flex items-center gap-2 mb-3">
      <div className={`w-8 h-8 rounded-md ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <h4 className="text-sm font-semibold text-white">{title}</h4>
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </div>
);

const InfoRow = ({ label, value, mono = false }) => (
  <div className="flex justify-between gap-2 py-1.5 border-b border-zinc-800/60 last:border-0 text-sm">
    <span className="text-zinc-500">{label}</span>
    <span className={`text-zinc-200 ${mono ? 'font-mono' : 'font-medium'} text-right`}>{value || '—'}</span>
  </div>
);

export const LocationAnalysisDialog = ({ property, open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [data, setData] = useState(null);

  const fetchAnalysis = useCallback(async () => {
    if (!property?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/properties/${property.id}/location-analysis`, { withCredentials: true });
      setData(res.data?.analysis);
    } catch {
      // Pas grave : analyse jamais lancée
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [property?.id]);

  useEffect(() => {
    if (open) fetchAnalysis();
  }, [open, fetchAnalysis]);

  const runAnalysis = async () => {
    if (!property?.id) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/properties/${property.id}/analyze-location`, {}, { withCredentials: true });
      setData(res.data);
      if (res.data?.success === false) {
        toast.error(res.data?.error || "Échec de l'analyse");
      } else {
        toast.success('Analyse terminée');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'analyse");
    } finally {
      setAnalyzing(false);
    }
  };

  const renderEmpty = () => (
    <div className="text-center py-12">
      <Map className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
      <p className="text-zinc-400 text-sm mb-4">
        Aucune analyse de localisation effectuée pour ce bien.
      </p>
      <p className="text-xs text-zinc-500 mb-6 max-w-md mx-auto">
        On va interroger les APIs gouvernementales gratuites pour récupérer le cadastre,
        le PLU, les risques officiels et les ventes immobilières comparables.
      </p>
      <Button
        onClick={runAnalysis}
        disabled={analyzing}
        className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"
        data-testid="run-analysis-btn"
      >
        {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Map className="w-4 h-4 mr-2" />}
        Lancer l'analyse cadastrale
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="location-analysis-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-lg">
            <MapPin className="w-5 h-5 text-amber-400" />
            Cadastre & Urbanisme
            <span className="text-zinc-500 font-normal text-sm ml-auto">
              {property?.address} · {property?.postal_code} {property?.city}
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-zinc-500"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>
        ) : !data ? (
          renderEmpty()
        ) : !data.success ? (
          <div className="text-center py-12 space-y-3">
            <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
            <p className="text-zinc-300">{data.error || "Échec de l'analyse"}</p>
            <Button onClick={runAnalysis} disabled={analyzing} variant="outline" className="border-zinc-700 text-zinc-300">
              {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Réessayer
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Header avec bouton refresh */}
            <div className="flex items-center justify-between text-xs text-zinc-500">
              <span>Analyse effectuée le {fmtDate(data.analyzed_at)}</span>
              <Button onClick={runAnalysis} disabled={analyzing} variant="ghost" size="sm" className="text-zinc-400 h-7" data-testid="refresh-analysis-btn">
                {analyzing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                Actualiser
              </Button>
            </div>

            <Tabs defaultValue="cadastre" className="w-full">
              <TabsList className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto">
                <TabsTrigger value="carte"><MapPinned className="w-3.5 h-3.5 mr-1.5" /> Carte</TabsTrigger>
                <TabsTrigger value="cadastre"><Map className="w-3.5 h-3.5 mr-1.5" /> Cadastre</TabsTrigger>
                <TabsTrigger value="plu"><FileText className="w-3.5 h-3.5 mr-1.5" /> PLU</TabsTrigger>
                <TabsTrigger value="risques"><ShieldAlert className="w-3.5 h-3.5 mr-1.5" /> Risques {data.risques?.total ? `(${data.risques.total})` : ''}</TabsTrigger>
                <TabsTrigger value="dvf"><BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Ventes comparables</TabsTrigger>
              </TabsList>

              {/* Carte interactive — coordonnées issues du géocodage BAN */}
              <TabsContent value="carte" className="mt-4 space-y-3">
                <PropertyMap
                  lat={property?.lat ?? data.geocode?.lat}
                  lon={property?.lon ?? data.geocode?.lng}
                  title={property?.title}
                  address={`${property?.address || ''}, ${property?.postal_code || ''} ${property?.city || ''}`}
                  height={420}
                  radius={250}
                />
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                  Localisation précise issue de la BAN (Base Adresse Nationale). Le cercle vert matérialise une zone de 250 m utile pour évaluer le quartier immédiat (commerces, transports, voisinage).
                </p>
              </TabsContent>

              {/* Cadastre */}
              <TabsContent value="cadastre" className="mt-4 space-y-3">
                <SectionCard icon={Home} title="Géocodage adresse (BAN)" color="bg-blue-600">
                  <InfoRow label="Adresse normalisée" value={data.geocode?.label} />
                  <InfoRow label="Commune" value={`${data.geocode?.city} (${data.geocode?.postcode})`} />
                  <InfoRow label="Code INSEE" value={data.geocode?.citycode} mono />
                  <InfoRow label="Précision" value={data.geocode?.score ? `${Math.round(data.geocode.score * 100)}%` : '—'} mono />
                </SectionCard>
                {data.cadastre ? (
                  <SectionCard icon={Map} title="Parcelle cadastrale (IGN)" color="bg-amber-600">
                    <InfoRow label="Section" value={data.cadastre.section} mono />
                    <InfoRow label="Numéro de parcelle" value={data.cadastre.numero} mono />
                    <InfoRow label="Feuille" value={data.cadastre.feuille} mono />
                    <InfoRow label="Surface terrain officielle" value={data.cadastre.contenance_m2 ? `${data.cadastre.contenance_m2.toLocaleString('fr-FR')} m²` : '—'} mono />
                    <InfoRow label="Commune" value={data.cadastre.nom_com} />
                    <InfoRow label="Département" value={data.cadastre.code_dep} mono />
                    <InfoRow label="Identifiant unique" value={data.cadastre.idu} mono />
                  </SectionCard>
                ) : (
                  <div className="text-center py-4 text-sm text-zinc-500">Parcelle non trouvée sur le cadastre.</div>
                )}
              </TabsContent>

              {/* PLU */}
              <TabsContent value="plu" className="mt-4 space-y-3">
                {data.plu ? (
                  <SectionCard
                    icon={FileText}
                    title="Plan Local d'Urbanisme (GPU IGN)"
                    color="bg-violet-600"
                    action={data.plu.urlfic ? (
                      <a href={data.plu.urlfic} target="_blank" rel="noreferrer" className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Règlement PDF
                      </a>
                    ) : null}
                  >
                    <InfoRow label="Zone PLU" value={<Badge className="bg-violet-600/30 text-violet-200 border-violet-500/40">{data.plu.libelle}</Badge>} />
                    <InfoRow label="Type de zone" value={data.plu.typezone === 'U' ? 'Urbaine' : data.plu.typezone === 'AU' ? 'À urbaniser' : data.plu.typezone === 'N' ? 'Naturelle' : data.plu.typezone === 'A' ? 'Agricole' : data.plu.typezone} />
                    <InfoRow label="Description" value={data.plu.libelong} />
                    <InfoRow label="Date d'approbation" value={fmtDate(data.plu.datappro)} />
                    <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
                      💡 Vérifiez le règlement PDF pour les règles précises (hauteur max, emprise au sol, COS, retraits...).
                    </p>
                  </SectionCard>
                ) : (
                  <div className="text-center py-4 text-sm text-zinc-500">PLU non disponible pour cette commune.</div>
                )}
              </TabsContent>

              {/* Risques */}
              <TabsContent value="risques" className="mt-4 space-y-3">
                {data.risques?.categories?.length > 0 ? (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-center">
                        <p className="text-xs text-zinc-500 mb-1">Risques majeurs</p>
                        <p className="text-2xl font-bold text-orange-300">{data.risques.total}</p>
                        <p className="text-[10px] text-zinc-500">identifiés sur la commune</p>
                      </div>
                      <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-center">
                        <p className="text-xs text-zinc-500 mb-1">Arrêtés CatNat</p>
                        <p className="text-2xl font-bold text-red-300">{data.risques.arretes_total ?? '—'}</p>
                        <p className="text-[10px] text-zinc-500">historique total</p>
                      </div>
                      <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-center">
                        <p className="text-xs text-zinc-500 mb-1">Zone sismique</p>
                        <p className="text-2xl font-bold text-white">{data.risques.zone_sismicite ?? '—'}</p>
                        <p className="text-[10px] text-zinc-500">{data.risques.zone_sismicite ? 'sur 5 (5 = max)' : 'non renseignée'}</p>
                      </div>
                    </div>

                    <SectionCard icon={ShieldAlert} title="Risques majeurs identifiés" color="bg-orange-600">
                      <div className="space-y-2">
                        {data.risques.categories.map((cat, idx) => (
                          <div key={cat.label + idx} className="flex items-start gap-3 py-2 border-b border-zinc-800/60 last:border-0">
                            <Badge className="bg-orange-600/20 text-orange-300 border-orange-500/40 flex-shrink-0">
                              {cat.arretes_count ?? 0} arrêté{(cat.arretes_count ?? 0) > 1 ? 's' : ''}
                            </Badge>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-zinc-200 font-medium">{cat.label}</p>
                              {cat.zone_sismicite && (
                                <p className="text-xs text-amber-300 mt-0.5">Zone sismicité {cat.zone_sismicite}</p>
                              )}
                              {cat.arretes?.length > 0 && (
                                <details className="mt-1.5">
                                  <summary className="text-[11px] text-zinc-500 cursor-pointer hover:text-zinc-300">
                                    Voir les {cat.arretes.length} dernier{cat.arretes.length > 1 ? 's' : ''} arrêté{cat.arretes.length > 1 ? 's' : ''}
                                  </summary>
                                  <ul className="mt-1.5 space-y-0.5 ml-2">
                                    {cat.arretes.map((a, i) => (
                                      <li key={i} className="text-[11px] text-zinc-500">
                                        • <span className="text-zinc-300">{a.libelle}</span>
                                        {a.date_debut && <span> — du {a.date_debut}{a.date_fin && a.date_fin !== a.date_debut ? ` au ${a.date_fin}` : ''}</span>}
                                        {a.date_arrete && <span className="text-zinc-600"> · publié {a.date_arrete}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </details>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </SectionCard>

                    {data.risques.arretes_recent?.length > 0 && (
                      <SectionCard icon={ShieldAlert} title="Derniers arrêtés de catastrophe naturelle" color="bg-red-600">
                        <div className="space-y-1.5 max-h-72 overflow-y-auto">
                          {data.risques.arretes_recent.map((a, i) => (
                            <div key={i} className="grid grid-cols-[1fr,110px,90px] gap-2 text-xs py-1.5 border-b border-zinc-800/60 last:border-0">
                              <span className="text-zinc-300 truncate">{a.libelle || '—'}</span>
                              <span className="text-zinc-500 text-right text-[11px]">
                                {a.date_debut}{a.date_fin && a.date_fin !== a.date_debut ? ` → ${a.date_fin}` : ''}
                              </span>
                              <span className="text-zinc-600 text-right text-[10px]">arr. {a.date_arrete}</span>
                            </div>
                          ))}
                        </div>
                      </SectionCard>
                    )}

                    <p className="text-[11px] text-zinc-500 leading-relaxed">
                      💡 Les arrêtés de catastrophes naturelles ont une incidence sur l'assurance multirisque habitation et peuvent imposer des études de sol (G2 obligatoire en zone argile gonflante par exemple).
                    </p>
                  </>
                ) : (
                  <div className="text-center py-6 text-sm text-emerald-400">
                    <span className="text-2xl">✓</span><br />
                    Aucun arrêté de catastrophe naturelle référencé sur cette commune.
                  </div>
                )}
              </TabsContent>

              {/* DVF */}
              <TabsContent value="dvf" className="mt-4 space-y-3">
                {data.dvf ? (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-center">
                        <p className="text-xs text-zinc-500 mb-1">Ventes trouvées</p>
                        <p className="text-2xl font-bold text-white">{data.dvf.count_total}</p>
                        <p className="text-[10px] text-zinc-500">dans {data.dvf.radius_m}m · {data.dvf.years} ans</p>
                      </div>
                      <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-center">
                        <p className="text-xs text-zinc-500 mb-1">Prix m² moyen</p>
                        <p className="text-2xl font-bold text-amber-300">{data.dvf.avg_price_m2 ? `${data.dvf.avg_price_m2.toLocaleString('fr-FR')} €` : '—'}</p>
                        <p className="text-[10px] text-zinc-500">{data.dvf.count_for_avg} ventes fiables</p>
                      </div>
                      <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-center">
                        <p className="text-xs text-zinc-500 mb-1">Votre prix m²</p>
                        <p className="text-2xl font-bold text-white">
                          {property?.surface && property?.price ? `${Math.round(property.price / property.surface).toLocaleString('fr-FR')} €` : '—'}
                        </p>
                        <p className="text-[10px] text-zinc-500">
                          {property?.surface && property?.price && data.dvf.avg_price_m2
                            ? (() => {
                                const pct = ((property.price / property.surface) / data.dvf.avg_price_m2 - 1) * 100;
                                return <span className={pct < 0 ? 'text-emerald-400' : 'text-orange-400'}>{pct >= 0 ? '+' : ''}{pct.toFixed(0)}% vs marché</span>;
                              })()
                            : '—'}
                        </p>
                      </div>
                    </div>

                    {data.dvf.mutations?.length > 0 ? (
                      <SectionCard icon={Euro} title="Top ventes proches" color="bg-emerald-600">
                        <div className="space-y-1.5 max-h-96 overflow-y-auto">
                          {data.dvf.mutations.slice(0, 15).map((m, i) => (
                            <div key={i} className="grid grid-cols-[1fr,80px,90px,90px,80px] gap-2 text-xs py-1.5 border-b border-zinc-800/60 last:border-0">
                              <div className="min-w-0 truncate">
                                <span className="text-zinc-300">{m.adresse || '—'}</span>
                                <span className="text-zinc-500 ml-1">· {m.commune}</span>
                              </div>
                              <span className="text-zinc-500 text-right">{m.type || '—'}</span>
                              <span className="text-zinc-400 text-right font-mono">{m.surface_bati ? `${m.surface_bati} m²` : '—'}</span>
                              <span className="text-amber-300 text-right font-mono">{fmtEur(m.valeur)}</span>
                              <span className="text-zinc-500 text-right text-[10px]">{fmtDate(m.date)}</span>
                            </div>
                          ))}
                        </div>
                      </SectionCard>
                    ) : (
                      <div className="text-center py-4 text-sm text-zinc-500">Aucune vente comparable trouvée.</div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6 space-y-2">
                    <BarChart3 className="w-10 h-10 text-zinc-700 mx-auto" />
                    <p className="text-sm text-zinc-400">API DVF temporairement indisponible.</p>
                    <p className="text-xs text-zinc-500">L'API gouvernementale est intermittente. Réessayez dans quelques heures.</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LocationAnalysisDialog;
